import React, { useRef, useState, useEffect } from "react";
import "./input.css";

/**
 * FloatingNumber
 *
 * - Displays formatted number with commas on blur (e.g., 4,000.00)
 * - Shows raw number for editing on focus (e.g., 4000.00)
 * - Allows only digits + one dot, max 2 decimal places
 * - Clears on Backspace when entire input is selected
 *
 * Props:
 *  - value (string|number) : raw numeric string or number (no commas preferred)
 *  - onChange (fn)         : receives { target: { value: "1234.56" } } (raw string)
 *  - onFocus, onBlur       : forwarded (we handle formatting around these)
 *  - label, type, ...props : forwarded to input
 */
export function FloatingNumber({
  label,
  type = "text",
  value = "",
  defaultValue = "",
  onFocus,
  onChange,
  onBlur,
  decimals = 2,
  allowNegative = false,
  ...props
}) {
  const inputRef = useRef(null);
  const lastCommittedValue = useRef("");
  const inputId = props.id || `input-${props.name || Math.random()}`;

  // internal display value (what goes into the input.value)
  const [displayValue, setDisplayValue] = useState(() =>
    formatNumberForDisplay(value, /*isFocused*/ false, decimals),
  );
  const [isFocused, setIsFocused] = useState(false);

  // Keep internal displayValue in sync when parent value changes.
  useEffect(() => {
    setDisplayValue(formatNumberForDisplay(value, isFocused, decimals));
    // formatNumberForDisplay is stable (defined in component); omit to avoid effect on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, defaultValue, decimals, isFocused]);

  // Utility: return raw string (no commas)
  function unformat(val) {
    if (val === null || val === undefined) return "";
    return String(val).replace(/,/g, "");
  }

  // Strip to digits + a single dot (plus a leading '-' when allowNegative, so a field
  // like Estimated Tax Prorations can show/type a credit vs. a debit), with no
  // decimal-length limit — shared by normalizeToDecimalsString (live typing, below) and
  // the display-path rounding in formatNumberForDisplay.
  function sanitizeNumericChars(raw) {
    const negative = allowNegative && /^\s*-/.test(raw);
    let s = raw.replace(/[^0-9.]/g, "");
    const parts = s.split(".");
    if (parts.length > 2) {
      s = parts[0] + "." + parts.slice(1).join("");
    }
    if (!negative) return s;
    // keep the lone '-' while the user hasn't typed a digit yet, instead of dropping it
    return s ? `-${s}` : "-";
  }

  // Utility: format for display (commas + exactly 2 decimal places if numeric)
  function formatNumberForDisplay(val, focused, decimals = 2) {
    const raw = unformat(val).trim();
    if (raw === "" || raw === ".") return "";

    // When focused, show raw editable string
    if (focused) {
      return normalizeToDecimalsString(
        raw,
        decimals,
        /*allowTrailingDot*/ true,
      );
    }

    // Committed/display value: parse the FULL-PRECISION raw value and let
    // toLocaleString round it properly, rather than going through
    // normalizeToDecimalsString's substring-based truncation (fine for
    // live typing, but it would chop a floating-point-noise value like
    // "2436.1099999999997" down to "2436.10" instead of correctly
    // rounding to "2436.11" — a cent-level mismatch against anywhere else
    // that rounds properly, e.g. the PDF).
    const cleaned = sanitizeNumericChars(raw);
    if (cleaned === "" || cleaned === ".") return "";

    const num = Number(cleaned);
    if (Number.isNaN(num)) return cleaned;

    return num.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  // Normalize a raw numeric string: remove leading zeros except "0", ensure at most two decimals
  // allowTrailingDot=true keeps a trailing '.' while editing (so user can type "123.")
  function normalizeToDecimalsString(
    raw,
    decimals = 2,
    allowTrailingDot = false,
  ) {
    if (!raw) return "";

    const s = sanitizeNumericChars(raw);

    const [intPart = "", decPart = ""] = s.split(".");
    let intNormalized = intPart.replace(/^0+(?=\d)/, "");
    if (intNormalized === "") intNormalized = intPart === "" ? "" : "0";

    let decNormalized = decPart;
    if (decimals === 0) {
      // ⛔ integer only
      return intNormalized;
    }

    if (decNormalized.length > decimals)
      decNormalized = decNormalized.substring(0, decimals);

    if (decPart === "" && s.includes(".") && allowTrailingDot) {
      return `${intNormalized}.`;
    }

    return decNormalized ? `${intNormalized}.${decNormalized}` : intNormalized;
  }

  // handle focus: show unformatted editable value and select all
  const handleFocus = (e) => {
    setIsFocused(true);
    const raw = unformat(value);
    setDisplayValue(formatNumberForDisplay(raw, /*isFocused*/ true, decimals));
    requestAnimationFrame(() => e.target.select());
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    const raw = unformat(displayValue);
    const formatted = formatNumberForDisplay(
      raw,
      /*isFocused*/ false,
      decimals,
    );
    setDisplayValue(formatted);
    // if (onBlur) onBlur(e);
    // RESTRICTION: only notify parent if the committed value changed
    if (onBlur && lastCommittedValue.current !== formatted) {
      lastCommittedValue.current = formatted;
      onBlur(e);
    }
  };

  // handle keydown for Backspace when all selected -> clear
  // keydown: treat Backspace/Delete when whole value selected as "clear -> 0"
  const handleKeyDown = (e) => {
    if (e.key === "Backspace" || e.key === "Delete") {
      const input = e.target;
      const isAllSelected =
        input.selectionStart === 0 && input.selectionEnd === input.value.length;

      if (isAllSelected) {
        e.preventDefault();

        // update visible display to 0 (or formatted "0.00")
        const displayZero = formatNumberForDisplay(0);
        setDisplayValue(displayZero);

        // notify parent: model value should be numeric 0 (or "0" if you expect string)
        if (onChange) onChange({ target: { value: 0 } });

        return;
      }
    }

    // allow other keys - you sanitize on change
  };

  // handle change: sanitize and limit decimals, then forward raw (unformatted) to parent
  const handleChange = (e) => {
    let val = e.target.value;
    // Accept pasted values with commas; sanitize everything except digits, dot, and
    // (when allowNegative) a leading minus sign — same rule sanitizeNumericChars uses.
    val = sanitizeNumericChars(val.replace(/,/g, ""));
    // Normalize to max 2 decimals and keep trailing dot if user typed it while focused
    const normalized = normalizeToDecimalsString(
      val,
      decimals,
      /*allowTrailingDot*/ isFocused,
    );
    setDisplayValue(normalized);
    // Notify parent with raw numeric string (no commas). If it's just "." or a lone
    // "-" (allowNegative, no digits typed yet), send empty string.
    const parentValue = normalized === "." || normalized === "-" ? "" : normalized;
    if (onChange) onChange({ target: { value: parentValue } });
  };

  return (
    <div className="floating-input-wrapper">
      <input
        ref={inputRef}
        id={inputId}
        type={type}
        value={displayValue}
        placeholder=" "
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        onBlur={handleBlur}
        inputMode="decimal"
        aria-label={props["aria-label"] || label}
        disabled={props.disabled}
        {...props}
      />
      {label && <label htmlFor={inputId}>{label}</label>}
    </div>
  );
}

export default FloatingNumber;
